import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { MousePointerClick } from "lucide-react";
import Header from "@/components/Header";
import QuoteBanner from "@/components/QuoteBanner";
import Footer from "@/components/Footer";
import TnMap from "@/components/TnMap";
import MapLegend from "@/components/MapLegend";
import SeatPicker from "@/components/SeatPicker";
import SeatTransition from "@/components/SeatTransition";
import TransitionSummary from "@/components/TransitionSummary";
import DistrictPicker from "@/components/DistrictPicker";
import DistrictSummary from "@/components/DistrictSummary";
import MlaCard from "@/components/MlaCard";
import FactsTab from "@/components/FactsTab";
import { useLang } from "@/context/LangContext";
import { fetchSeats, fetchFunFacts } from "@/lib/insights";

function ModeToggle({ mode, setMode }) {
  const { t, lang } = useLang();
  const opt = (k, label) => (
    <button
      onClick={() => setMode(k)}
      className={`px-4 py-2 rounded-full text-[13px] font-semibold transition-colors ${mode === k ? "bg-[#1a1640] text-white" : "text-[#1a1640] hover:bg-[#f3eedc]"} ${lang === "ta" ? "font-tamil" : ""}`}
      data-testid={`map-mode-${k}`}
    >
      {label}
    </button>
  );
  return (
    <div className="inline-flex rounded-full bg-white p-1 shadow-sm" data-testid="map-mode-toggle">
      {opt("change", t("mode_change"))}
      {opt("party", t("mode_party"))}
    </div>
  );
}

function EmptyPanel({ text }) {
  const { lang } = useLang();
  return (
    <div className={`bg-white/70 rounded-[22px] p-8 text-center text-[#6b6894] ${lang === "ta" ? "font-tamil" : ""}`} data-testid="map-empty-panel">
      <MousePointerClick className="mx-auto mb-3 text-[#8c89ad]" />
      {text}
    </div>
  );
}

function LeadersPanel({ seat, district, visible, pick, t, ta }) {
  return (
    <>
      {seat ? <MlaCard seat={seat} /> : !district && <EmptyPanel text={t("leaders_prompt")} />}
      {district && (
        <section data-testid="district-mlas">
          <h3 className={`mb-2 text-[15px] font-bold text-[#1a1640] ${ta ? "font-tamil" : ""}`}>{t("district_mlas")} · {visible.length}</h3>
          <div className="space-y-2">{visible.map((s) => <MlaCard key={s.ac_no} seat={s} compact onPick={pick} />)}</div>
        </section>
      )}
    </>
  );
}

function ChangesPanel({ seat, district, visible, selected, pick, t }) {
  return (
    <>
      {district && visible.length > 0 && <DistrictSummary seats={visible} selected={selected} onPick={pick} />}
      {seat ? <SeatTransition seat={seat} showLink /> : <EmptyPanel text={t("tap_seat_prompt")} />}
      {visible.length > 0 && <TransitionSummary seats={visible} />}
    </>
  );
}

export default function MapPage({ variant = "changes" }) {
  const { t, lang } = useLang();
  const ta = lang === "ta";
  const leaders = variant === "leaders";
  const [params] = useSearchParams();
  const { hash } = useLocation();
  const [seats, setSeats] = useState([]);
  const [facts, setFacts] = useState(null);
  const [mode, setMode] = useState(leaders ? "party" : "change");
  const [selected, setSelected] = useState(null);
  const [focus, setFocus] = useState(null);
  const [district, setDistrict] = useState(null);

  useEffect(() => {
    fetchSeats().then(setSeats);
    if (!leaders) fetchFunFacts().then(setFacts);
  }, [leaders]);
  const byAc = useMemo(() => Object.fromEntries(seats.map((s) => [s.ac_no, s])), [seats]);
  const seat = byAc[selected];
  const visible = useMemo(() => (district ? seats.filter((s) => s.district === district) : seats), [seats, district]);

  function pick(ac, zoom = true) {
    setSelected(ac);
    const leaving = district && byAc[ac]?.district !== district;
    if (leaving) setDistrict(null);
    if (zoom || leaving) setFocus({ ac, n: Date.now() });
    if (window.innerWidth < 1024) setTimeout(() => document.getElementById("seat-panel")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  }

  useEffect(() => {
    const ac = Number(params.get("seat"));
    if (ac && byAc[ac]) { setSelected(ac); setFocus({ ac, n: Date.now() }); }
  }, [params, byAc]);

  useEffect(() => {
    if (hash === "#facts" && facts) setTimeout(() => document.getElementById("facts")?.scrollIntoView({ behavior: "smooth" }), 100);
  }, [hash, facts]);

  const panelProps = { seat, district, visible, selected, pick, t, ta };
  return (
    <div className="App">
      <Header compact={!leaders} title={leaders ? undefined : t("nav_changes")} />
      <QuoteBanner />
      <main className="max-w-6xl mx-auto px-6 pt-6 pb-16">
        <h2 className={`mt-4 text-3xl sm:text-4xl font-semibold text-[#1a1640] tracking-tight ${ta ? "font-tamil" : "font-display"}`} data-testid="map-title">
          {t(leaders ? "leaders_title" : "changes_title")}
        </h2>
        <p className={`mt-2 text-[15px] text-[#6b6894] ${ta ? "font-tamil" : ""}`}>{t(leaders ? "leaders_sub" : "map_sub")}</p>

        <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
          {!leaders && <ModeToggle mode={mode} setMode={setMode} />}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full min-w-0 sm:w-[34rem] sm:ml-auto [&>*]:min-w-0">
            <DistrictPicker seats={seats} district={district} onChange={(d) => { setDistrict(d); if (d && byAc[selected]?.district !== d) setSelected(null); }} />
            <SeatPicker seats={visible} selected={selected} onPick={pick} />
          </div>
        </div>
        <div className="mt-4"><MapLegend seats={visible} mode={mode} /></div>

        <div className="mt-5 grid lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] gap-6 items-start">
          <div>
            <TnMap seatsByAc={byAc} mode={mode} selected={selected} onSelect={(ac) => pick(ac, false)} focus={focus} district={district} />
            <p className={`mt-2 text-[12px] text-[#8c89ad] ${ta ? "font-tamil" : ""}`}>{t("map_hint")}</p>
          </div>
          <div id="seat-panel" className="space-y-4 scroll-mt-4">
            {leaders ? <LeadersPanel {...panelProps} /> : <ChangesPanel {...panelProps} />}
          </div>
        </div>
        <p className={`mt-8 text-[11.5px] leading-relaxed text-[#8c89ad] ${ta ? "font-tamil" : ""}`} data-testid="map-source-note">{t("map_source")}</p>

        {!leaders && (
          <section id="facts" className="mt-12 scroll-mt-4" data-testid="changes-facts">
            <h2 className={`text-2xl sm:text-3xl font-semibold text-[#1a1640] ${ta ? "font-tamil" : "font-display"}`}>{t("facts_title")}</h2>
            <p className={`mt-1 mb-5 text-[14px] text-[#6b6894] ${ta ? "font-tamil" : ""}`}>{t("facts_sub")}</p>
            {ta && <p className="-mt-3 mb-4 text-[12px] text-[#8c89ad] font-tamil" data-testid="ai-translation-note">{t("ai_note")}</p>}
            {facts && <FactsTab data={facts} seats={seats} />}
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
